import { useEffect } from 'react';
import Footer from '../home/components/Footer';

export default function ProviderTermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-500 to-teal-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl font-bold mb-4">Provider Terms & Conditions</h1>
          <p className="text-teal-50 text-lg">Last updated: 15 January 2025</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed mb-8">
            These Provider Terms & Conditions ("Terms") govern your access to and use of the Taskbudy platform as a provider ("Provider", "you", "your"). By registering, accessing, or using the Platform, you agree to these Terms.
          </p>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. DEFINITIONS</h2>
            <ul className="space-y-2 text-gray-700">
              <li><strong>"Taskbudy" / "we" / "us"</strong> means Taskbudy Ltd.</li>
              <li><strong>"Platform"</strong> means the Taskbudy website, mobile application, dashboards, and related services.</li>
              <li><strong>"Provider"</strong> means a self-employed independent contractor offering services to Customers via the Platform.</li>
              <li><strong>"Customer"</strong> means a user requesting services through the Platform.</li>
              <li><strong>"Job"</strong> means a task requested by a Customer and accepted by a Provider.</li>
              <li><strong>"Subscription"</strong> means the provider access fee.</li>
              <li><strong>"Commission"</strong> means the percentage fee deducted from completed Jobs.</li>
              <li><strong>"Earnings"</strong> means amounts payable to a Provider after deductions.</li>
              <li><strong>"Starter Kit"</strong> means the Taskbudy-branded workwear kit required for activation.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. PROVIDER STATUS (SELF-EMPLOYED)</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>2.1</strong> Providers operate strictly as self-employed independent contractors.</p>
              <p><strong>2.2</strong> Nothing in these Terms creates an employment, worker, agency, partnership, or joint venture relationship.</p>
              <p><strong>2.3</strong> Providers are not entitled to employment rights or benefits.</p>
              <p><strong>2.4</strong> Providers control their availability and job acceptance.</p>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. TAXES & FINANCIAL RESPONSIBILITY</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>3.1</strong> Providers are responsible for all taxes, including Income Tax, National Insurance, and VAT (if applicable).</p>
              <p><strong>3.2</strong> Taskbudy does not deduct or withhold taxes on behalf of Providers.</p>
              <p><strong>3.3</strong> Providers agree to indemnify Taskbudy against claims arising from tax non-compliance.</p>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. ELIGIBILITY & ACCOUNT INFORMATION</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>4.1</strong> Providers must be at least 18 and legally entitled to work in the UK.</p>
              <p><strong>4.2</strong> Information provided must be accurate and kept up to date.</p>
              <p><strong>4.3</strong> Taskbudy may refuse, suspend, or terminate accounts where permitted by law.</p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. VERIFICATION & DOCUMENTATION</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>5.1</strong> All Providers must complete third-party ID verification.</p>
              <p><strong>5.2</strong> Mandatory for all Providers:</p>
              <ul className="list-disc pl-8 space-y-1">
                <li>Public liability insurance</li>
                <li>Up-to-date DBS check</li>
              </ul>
              <p><strong>5.3</strong> Trade-specific requirements:</p>
              <ul className="list-disc pl-8 space-y-1">
                <li><strong>Plumbers & Electricians:</strong> qualifications + CSCS card</li>
                <li><strong>Waste Removal:</strong> Waste Carrier Licence + CSCS card</li>
                <li><strong>Transport & Moving:</strong> correct vehicle / goods-in-transit insurance</li>
                <li><strong>Handyman:</strong> CSCS card</li>
              </ul>
              <p><strong>5.4</strong> Documents must remain valid. Expired or false documents may result in suspension.</p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. JOB DISPATCH & ACCEPTANCE</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>6.1</strong> Jobs are sent based on location and category.</p>
              <p><strong>6.2</strong> Jobs are assigned on a first-to-accept basis.</p>
              <p><strong>6.3</strong> Providers may decline any job.</p>
              <p><strong>6.4</strong> Accepted jobs must be completed professionally and as agreed.</p>
            </div>
          </section>

          {/* Note for continuation */}
          <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mt-12">
            <p className="text-gray-700 italic">
              Additional sections will be added upon receipt of the complete terms document.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
